import React, { useEffect, useRef, useState } from 'react';

import './stylesSportsbook.css';

import CustomDatePicker from '../../CommonComponents/CustomDatePicker/CustomDatePicker';

import dayjs from 'dayjs';
import ProviderPerformance from '../ProviderPerformance/ProviderPerformance';
import createPrivateAdminPanelInstance from '../../../api/PrivateAdminPanelInstance';
import { API_ROUTES } from '../../../api/ApiRoutes';
import CustomAutoCompleteControlled from '../../CommonComponents/CustomAutoComplete/CustomAutoCompleteControlled';
import MarginByBetType from './MarginByBetType/MarginByBetType';
import LeagueDistribution from './LeagueDistribution/LeagueDistribution';
import TicketLengthBreakdown from './TicketLengthBreakdown/TicketLengthBreakdown';
import SportSelectionDistribution from './SportSelectionDistribution/SportSelectionDistribution';
import TicketLengthDistribution from './TicketLengthDistribution/TicketLengthDistribution';
import TotalLifecycleStages from './TotalLifecycleStages/TotalLifecycleStages';
import SportPerformance from './SportPerformance/SportPerformance';
import Margin from './Margin/Margin';
import NumberOfBetsByType from './NumberOfBetsByType/NumberOfBetsByType';
import TurnoverByType from './TurnoverByType/TurnoverByType';
import PrematchTicketBreakdown from './PrematchTicketBreakdown/PrematchTicketBreakdown';
import UsersByState from './UsersByState/UsersByState';
import PrematchStakeBreakdown from './PrematchStakeBreakdown/PrematchStakeBreakdown';

function SportsbookKpi({ activeTab, setActiveTab }) {
  const token = JSON.parse(localStorage.getItem('token'));
  const PrivateAdminPanelInstance = createPrivateAdminPanelInstance(
    token?.IdToken
  );

  const isFirstLoad = useRef(true);

  const thisMonthRange = [dayjs().startOf('month'), dayjs().endOf('day')];

  const [isApiRunning, setIsApiRunning] = useState(false);
  const [sportsBookDates, setSportsBookDates] = useState(thisMonthRange);

  const [provider, setProvider] = useState([
    {
      label: 'sportradar',
      value: 'sportradar',
    },
    {
      label: 'dst',
      value: 'dst',
    },
    {
      label: 'oddin',
      value: 'oddin',
    },
  ]);
  const [sport, setSport] = useState([]);
  const [league, setLeague] = useState([]);
  const [match, setMatch] = useState([
    {
      label: 'Match 1',
      value: 'Match 1',
    },
    {
      label: 'Match 2',
      value: 'Match 2',
    },
  ]);

  const [selectedFilters, setSelectedFilters] = useState({
    provider: null,
    sport: null,
    league: null,
    match: null,
  });

  const [sportsbookOverview, setSportsbookOverview] = useState({
    turnover_graph: {},
    turnover: 0,
    ggr: 0,
    total_bets: 0,
    margin: 0,
    num_events: 0,
    avg_bet_amount: 0,
    num_players_engaged: 0,
    cash_out_profit: 0,
  });

  const [totalEvents, setTotalEvents] = useState({
    data: [],
  });

  const [betTimingAnalysis, setBetTimingAnalysis] = useState({});

  const [selectedProvider, setSelectedProvider] = useState(
    selectedFilters.provider
  );

  useEffect(() => {
    getSportsBookData(thisMonthRange, false);
  }, []);

  const getSportsBookData = async (
    queryString = thisMonthRange,
    isQueryString = true
  ) => {
    setIsApiRunning(true);

    let startDate = '';
    let endDate = '';

    if (isQueryString) {
      let formattedQueryString = queryString.split('&');
      startDate = dayjs(formattedQueryString[0]).format('YYYY-MM-DD');
      endDate = dayjs(formattedQueryString[1]).format('YYYY-MM-DD');
    } else {
      startDate = queryString[0]?.format('YYYY-MM-DD');
      endDate = queryString[1]?.format('YYYY-MM-DD');
    }

    const params = {
      start_date: startDate,
      end_date: endDate,
      odds_provider: selectedFilters?.provider,
      sport_name: selectedFilters?.sport,
      league_name: selectedFilters?.league,
      match: selectedFilters?.match,
    };

    let totalEventsParams = {};
    if (!isFirstLoad.current) {
      totalEventsParams.start_date = startDate;
      totalEventsParams.end_date = endDate;
    }

    try {
      const requests = [
        PrivateAdminPanelInstance?.post(
          API_ROUTES?.GET_SPORTSBOOK_OVERVIEW,
          params
        ),
        PrivateAdminPanelInstance?.post(
          API_ROUTES?.GET_TOTAL_EVENTS_DATA,
          totalEventsParams
        ),
        PrivateAdminPanelInstance?.post(API_ROUTES?.GET_BET_TIMING_ANALYSIS, {
          start_date: startDate,
          end_date: endDate,
        }),
      ];

      const responses = await Promise.allSettled(requests);

      const [
        sportsbookOverviewResponse,
        totalEventsResponse,
        betTimingAnalysisResponse,
      ] = responses;

      const sportsbookOverviewData =
        sportsbookOverviewResponse?.value?.data?.data;
      const totalEventsData = totalEventsResponse?.value?.data?.data;
      const betTimingAnalysisData =
        betTimingAnalysisResponse?.value?.data?.data;

      if (
        sportsbookOverviewResponse?.status === 'fulfilled' &&
        sportsbookOverviewData
      ) {
        let data = sportsbookOverviewData;
        const sports = data?.sport_names?.map((sport) => {
          return {
            label: sport,
            value: sport,
          };
        });
        const leagues = data?.league_names?.map((league) => {
          return {
            label: league,
            value: league,
          };
        });

        setSport(sports || []);
        setLeague(leagues || []);
        setSportsbookOverview(data);
        setSelectedProvider(selectedFilters?.provider);
      }

      if (totalEventsResponse?.status === 'fulfilled') {
        isFirstLoad.current = false;
        setTotalEvents(totalEventsData);
      }

      if (betTimingAnalysisResponse?.status === 'fulfilled') {
        setBetTimingAnalysis(betTimingAnalysisData);
      }
    } catch (error) {
      console.log('error fetching sportsbook data', error);
    } finally {
      setIsApiRunning(false);
    }
  };

  const handleClearFilters = () => {
    setSelectedFilters({
      provider: null,
      sport: null,
      league: null,
      match: null,
    });
  };

  const handleChangeProvider = (value) => {
    setSelectedFilters({ ...selectedFilters, provider: value });
  };

  const handleChangeSport = (value) => {
    setSelectedFilters({ ...selectedFilters, sport: value });
  };

  const handleChangeLeague = (value) => {
    setSelectedFilters({ ...selectedFilters, league: value });
  };

  const handleChangeMatch = (value) => {
    setSelectedFilters({ ...selectedFilters, match: value });
  };

  const handleClearProvider = () => {
    setSelectedFilters({ ...selectedFilters, provider: null });
  };

  const handleClearSport = () => {
    setSelectedFilters({ ...selectedFilters, sport: null });
  };

  const handleClearLeague = () => {
    setSelectedFilters({ ...selectedFilters, league: null });
  };

  const handleClearMatch = () => {
    setSelectedFilters({ ...selectedFilters, match: null });
  };

  return (
    <div className="sportsbook-container">
      <div className="sportsbook">
        <>
          <div className="head-controls">
            <div className="header-text-container">
              <p className="header-text">Sportsbook</p>
            </div>
            <CustomDatePicker
              getData={getSportsBookData}
              dates={sportsBookDates}
              setDates={setSportsBookDates}
              showPresets={true}
            />
          </div>
          <div className="sportsbook-tabs">
            <button
              className={activeTab === 'Sportsbook Kpi' ? 'active' : null}
              onClick={() => setActiveTab('Sportsbook Kpi')}
            >
              Sportsbook KPI
            </button>
            <button
              className={activeTab === 'Events List' ? 'active' : null}
              onClick={() => setActiveTab('Events List')}
            >
              Events List
            </button>
            <button
              className={activeTab === 'Latest Bets List' ? 'active' : null}
              onClick={() => setActiveTab('Latest Bets List')}
            >
              Latest Bets List
            </button>
          </div>
          <div className="sportsbook-content">
            <div className="sportsbook-selectors">
              <div className="sportsbook-selector">
                <span>Provider</span>
                <CustomAutoCompleteControlled
                  value={selectedFilters?.provider}
                  onChange={handleChangeProvider}
                  placeholder="Not Selected"
                  handleChange={handleChangeProvider}
                  clearOptions={handleClearProvider}
                  selector="value"
                  allOptions={provider}
                />
              </div>
              <div className="sportsbook-selector">
                <span>Sport</span>
                <CustomAutoCompleteControlled
                  value={selectedFilters?.sport}
                  onChange={handleChangeSport}
                  placeholder="Not Selected"
                  handleChange={handleChangeSport}
                  clearOptions={handleClearSport}
                  selector="value"
                  allOptions={sport}
                />
              </div>
              <div className="sportsbook-selector">
                <span>League</span>
                <CustomAutoCompleteControlled
                  value={selectedFilters?.league}
                  onChange={handleChangeLeague}
                  placeholder="Not Selected"
                  handleChange={handleChangeLeague}
                  clearOptions={handleClearLeague}
                  selector="value"
                  allOptions={league}
                />
              </div>
              {/* <div className="sportsbook-selector">
                  <span>Match</span>
                  <CustomAutoCompleteControlled
                    value={selectedFilters?.match}
                    placeholder="Not Selected"
                    handleChange={handleChangeMatch}
                    clearOptions={handleClearMatch}
                    selector="value"
                    allOptions={match}
                  />
                </div> */}
            </div>
            <div className="sportsbook-selector-action-buttons">
              <button className="clear" onClick={handleClearFilters}>
                Clear Filters
              </button>
              <button
                className="apply"
                onClick={() => getSportsBookData(sportsBookDates, false)}
              >
                Apply
              </button>
            </div>
            <div className="sportsbook-kpis">
              <div className="row">
                <ProviderPerformance
                  isApiRunning={isApiRunning}
                  title={selectedProvider}
                  providerData={sportsbookOverview}
                  totalEvents={totalEvents}
                />
              </div>
              <div className="row">
                <MarginByBetType />
              </div>
              <div className="row">
                <LeagueDistribution />
              </div>
              <div
                className="row"
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  gap: '20px',
                }}
              >
                <TicketLengthBreakdown />
                <SportSelectionDistribution />
              </div>
              <div className="row">
                <TicketLengthDistribution />
              </div>
              <div
                className="row"
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  gap: '20px',
                }}
              >
                <TotalLifecycleStages />
                <PrematchTicketBreakdown
                  betTimingAnalysis={betTimingAnalysis.ticket_breakdown}
                  isApiRunning={isApiRunning}
                />
              </div>
              <div
                className="row"
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  gap: '20px',
                }}
              >
                <UsersByState />
                <PrematchStakeBreakdown
                  betTimingAnalysis={betTimingAnalysis.stake_breakdown}
                  isApiRunning={isApiRunning}
                />
              </div>
              <div
                className="row"
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  gap: '20px',
                }}
              >
                <SportPerformance />
                <Margin />
              </div>
              <div
                className="row"
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  gap: '20px',
                }}
              >
                <NumberOfBetsByType />
                <TurnoverByType />
              </div>
            </div>
          </div>
        </>
      </div>
    </div>
  );
}

export default SportsbookKpi;
